// Slug derivation for equation modules. Mirrors _slugify / _row_slugs in
// core/pathway/builder.py — the backend derives these from the same labels,
// so any divergence here shows up as a balance that references an identifier
// the parser doesn't know about.

// Parenthetical content is stripped before slugifying: a label like
// "Electricity load (per kg NG)" becomes the slug `electricity_load`, NOT
// `electricity_load_per_kg_ng`. This is the v1 workaround for the lack of
// species-typed compound units: the agent (or user) can clarify the
// species basis of a `kWh/kg`-style param in the visible label without
// polluting the slug used in balance expressions.
//
// NFKD folds subscripts/superscripts to plain digits and splits accents into
// combining marks, which are then dropped — without it the species label
// "CO₂" slugs to `co` (colliding with carbon monoxide) instead of `co2`.
export function slugifyOne(label) {
  if (!label) return '';
  const folded = label.normalize('NFKD').replace(/\p{M}/gu, '');
  const stripped = folded.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  let s = stripped.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();
  if (s && /^\d/.test(s)) s = '_' + s;
  return s || 'x';
}

// Slugs for form rows, keeping any `name` a row already carries: a saved
// module's names are what its balance refers to, so a duplicate keeps them
// even after its labels are edited. Rows without a `name` derive one from
// their label, and two rows with the same slug get _2, _3, ... suffixes,
// skipping names other rows already carry.
export function rowSlugs(rows) {
  const list = rows ?? [];
  const seen = new Set(list.map(r => r?.name).filter(Boolean));
  return list.map(row => {
    if (row?.name) return row.name;
    const base = slugifyOne(row?.label || '');
    let slug = base;
    let i = 2;
    while (seen.has(slug)) {
      slug = `${base}_${i}`;
      i++;
    }
    seen.add(slug);
    return slug;
  });
}
